import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import dayjs from "dayjs";
import { HelpTextEditor } from "./HelpTextEditor";
import { Skeleton } from "@/components/ui/skeleton";

// Define the field help text type
interface FieldHelpText {
  id: string;
  custom_text: string | null;
  default_text: string;
  updated_at: string;
}

// Define the field type with help texts
interface Field {
  id: string;
  name: string;
  type: string;
  field_help_texts?: FieldHelpText[];
}

export const FieldHelpTextList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  // Fetch all fields
  const { data: fields, isLoading } = useQuery({
    queryKey: ["fields"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fields")
        .select("id, name, type, field_help_texts(id, custom_text, default_text, updated_at)")
        .order("name");

      if (error) throw error;
      return data as Field[];
    },
  });

  // Filter fields based on search term
  const filteredFields = fields?.filter((field) => field.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleEditHelpText = (fieldId: string) => {
    setSelectedFieldId(fieldId);
  };

  const handleCloseEditor = () => {
    setSelectedFieldId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search fields..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        // Loading skeleton
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="w-full">
              <CardHeader className="p-4">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-2/3" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFields?.map((field) => (
            <Card key={field.id} className="w-full">
              <CardHeader className="py-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{field.name}</CardTitle>
                    <CardDescription>Type: {field.type}</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => handleEditHelpText(field.id)}>
                    {field.field_help_texts?.length ? "Edit" : "Add"} Help Text
                  </Button>
                </div>
              </CardHeader>
              {field.field_help_texts && field.field_help_texts.length > 0 && (
                <CardContent className="pt-0">
                  <div className="text-sm text-muted-foreground">
                    <strong>Current Help Text:</strong>
                    <p className="mt-1">
                      {field.field_help_texts[0].custom_text || field.field_help_texts[0].default_text}
                    </p>
                    <p className="text-xs mt-2">
                      Last updated: {dayjs(field.field_help_texts[0].updated_at).tz().format("DD/MM/YYYY")}
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {selectedFieldId && (
        <HelpTextEditor
          fieldId={selectedFieldId}
          field={fields?.find((f) => f.id === selectedFieldId)}
          onClose={handleCloseEditor}
        />
      )}
    </div>
  );
};
